import { z } from 'zod';

// -----------------------------------------------------------------------------
// Enums
// -----------------------------------------------------------------------------

export const InstallmentPlanStatusSchema = z.enum(['draft', 'active', 'paid', 'defaulted', 'cancelled']);
export type InstallmentPlanStatus = z.infer<typeof InstallmentPlanStatusSchema>;

export const InstallmentScheduleStatusSchema = z.enum(['pending', 'paid', 'overdue', 'partial', 'written_off']);
export type InstallmentScheduleStatus = z.infer<typeof InstallmentScheduleStatusSchema>;

export const InstallmentFrequencySchema = z.enum(['monthly', 'biweekly', 'weekly', 'custom']);
export type InstallmentFrequency = z.infer<typeof InstallmentFrequencySchema>;

export const LateFeeKindSchema = z.enum(['none', 'fixed', 'percent']);
export type LateFeeKind = z.infer<typeof LateFeeKindSchema>;

const positiveDecimal = z.string().regex(/^\d+(\.\d{1,4})?$/, 'expected non-negative decimal');
const rate = z.string().regex(/^\d+(\.\d{1,4})?$/, 'expected non-negative rate (0..n)');
const currencyCode = z.string().length(3).regex(/^[A-Z]{3}$/);
const dateString = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

// -----------------------------------------------------------------------------
// Installment plan
// -----------------------------------------------------------------------------

export const InstallmentPlanCreateSchema = z.object({
  partyId: z.string().uuid(),
  invoiceId: z.string().uuid().nullable().optional(),
  currencyCode,
  principal: positiveDecimal,
  markupRate: rate.optional(),
  installmentCount: z.number().int().min(1).max(120),
  frequency: InstallmentFrequencySchema.optional(),
  customIntervalDays: z.number().int().min(1).max(365).nullable().optional(),
  firstDueDate: dateString,
  graceDays: z.number().int().min(0).max(180).optional(),
  lateFeeKind: LateFeeKindSchema.optional(),
  lateFeeAmount: positiveDecimal.optional(),
  notes: z.string().max(2_000).nullable().optional(),
});
export type InstallmentPlanCreate = z.infer<typeof InstallmentPlanCreateSchema>;

export const InstallmentPlanUpdateSchema = z.object({
  notes: z.string().max(2_000).nullable().optional(),
  graceDays: z.number().int().min(0).max(180).optional(),
  lateFeeKind: LateFeeKindSchema.optional(),
  lateFeeAmount: positiveDecimal.optional(),
});
export type InstallmentPlanUpdate = z.infer<typeof InstallmentPlanUpdateSchema>;

export const InstallmentPlanSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  planNumber: z.string(),
  partyId: z.string().uuid(),
  invoiceId: z.string().uuid().nullable(),
  currencyCode: z.string(),
  principal: z.string(),
  markupRate: z.string(),
  total: z.string(),
  installmentCount: z.number().int(),
  frequency: InstallmentFrequencySchema,
  customIntervalDays: z.number().int().nullable(),
  firstDueDate: z.string(),
  status: InstallmentPlanStatusSchema,
  graceDays: z.number().int(),
  lateFeeKind: LateFeeKindSchema,
  lateFeeAmount: z.string(),
  notes: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type InstallmentPlan = z.infer<typeof InstallmentPlanSchema>;

// -----------------------------------------------------------------------------
// Installment schedule row
// -----------------------------------------------------------------------------

export const InstallmentScheduleSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  planId: z.string().uuid(),
  installmentNo: z.number().int(),
  dueDate: z.string(),
  amountDue: z.string(),
  amountPaid: z.string(),
  lateFeeAccrued: z.string(),
  status: InstallmentScheduleStatusSchema,
  paidAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type InstallmentScheduleRow = z.infer<typeof InstallmentScheduleSchema>;

// -----------------------------------------------------------------------------
// Payment record
// -----------------------------------------------------------------------------

export const InstallmentPaymentInputSchema = z.object({
  amount: positiveDecimal,
  paymentDate: dateString,
  cashAccountId: z.string().uuid().optional(),
  reference: z.string().max(64).optional(),
});
export type InstallmentPaymentInput = z.infer<typeof InstallmentPaymentInputSchema>;

// -----------------------------------------------------------------------------
// Schedule row write-off
// -----------------------------------------------------------------------------

export const InstallmentWriteOffSchema = z.object({
  reason: z.string().max(500).optional(),
});
export type InstallmentWriteOff = z.infer<typeof InstallmentWriteOffSchema>;

export const InstallmentWriteOffResultSchema = z.object({
  scheduleRow: InstallmentScheduleSchema,
  planStatus: InstallmentPlanStatusSchema,
  writtenOffAmount: z.string(),
});
export type InstallmentWriteOffResult = z.infer<typeof InstallmentWriteOffResultSchema>;
